import React, { useEffect, useState, useMemo } from 'react';
import { Form, Input, Select, Row, Col, Card, Button, Modal, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import backIcon from "../../../../../Images/addstarsbackIcon.png";
import "./starsAdd.css";
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { Get_Expert_Video, Get_Not_Expert_Profile, Update_Expert, GET_NOT_EXPERT_EMPID_LOCATION_FULLNAME } from '../../../../../services/userAPI';
import SelectDropdown from 'react-select';
import expandIcon from "../../../../../Images/expandAddStar.png"
import PageBreadcrumb from '../../../../../reusableField/PageBreadcrumb';

const { TextArea } = Input;

const StarsAdd = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const name = location.state?.name;
    const accountName = location.state?.accountName;
    const isEdit = location.state?.isEdit;
    const isEditMode = location.state?.isEdit || false;
    const editData = location.state?.editData || null;

    const [discardModalVisible, setDiscardModalVisible] = useState(false);
    const [formInitialized, setFormInitialized] = useState(false);

    const accountId =
        location.state?.accountId ||
        location.state?.editData?.accountId ||
        location.state?.editData?.stars?.[0]?.accountId;

    const [updateExpert, { loading }] = useMutation(Update_Expert);

    const [stars, setStars] = useState([
        {
            id: 1,
            starId: null, // Store userId
            starDescription: '',
            youtubeCode: '',
            videoThumbnailCode: '',
            videoTitle: '',
            videoDescription: '',
            videoLanguageId: 1,
            expanded: true
        }
    ]);

    useEffect(() => {
        if (isEdit && editData?.stars?.length > 0) {
            setStars(editData.stars.map((star, index) => ({
                id: index + 1,
                starId: star.userId,
                starDescription: star.starDescription,
                youtubeCode: star.youtubeCode,
                videoThumbnailCode: star.videoThumbnailCode,
                videoTitle: star.videoTitle,
                videoDescription: star.videoDescription,
                videoLanguageId: star.videoLanguageId || 1,
                expanded: index === 0
            })));
        }
    }, [isEdit, editData]);

    const toggleExpand = (id) => {
        setStars(stars.map(star =>
            star.id === id ? { ...star, expanded: !star.expanded } : { ...star, expanded: false }
        ));
    };

    const addAnotherStar = async (add) => {
        try {
            const fieldsToValidate = stars
                .map((star, index) => {
                    if (!star.expanded) return [];
                    return [
                        ['stars', index, 'starId'],
                        ['stars', index, 'starDescription'],
                        ['stars', index, 'youtubeCode'],
                        ['stars', index, 'videoTitle'],
                        ['stars', index, 'videoThumbnailCode'],
                        ['stars', index, 'videoLanguage'],
                        ['stars', index, 'duration'],
                        ['stars', index, 'videoDescription']
                    ];
                })
                .flat();

            if (fieldsToValidate.length === 0) {
                add();
                const newId = stars.length > 0 ? Math.max(...stars.map(s => s.id)) + 1 : 1;
                setStars([
                    ...stars.map(star => ({ ...star, expanded: false })),
                    {
                        id: newId,
                        starId: null,
                        starDescription: '',
                        youtubeCode: '',
                        videoThumbnailCode: '',
                        videoTitle: '',
                        videoDescription: '',
                        videoLanguageId: 1,
                        expanded: true
                    }
                ]);
                return;
            }

            await form.validateFields(fieldsToValidate);

            add();
            const newId = stars.length > 0 ? Math.max(...stars.map(s => s.id)) + 1 : 1;
            setStars([
                ...stars.map(star => ({ ...star, expanded: false })),
                {
                    id: newId,
                    starId: null,
                    starDescription: '',
                    youtubeCode: '',
                    videoThumbnailCode: '',
                    videoTitle: '',
                    videoDescription: '',
                    videoLanguageId: 1,
                    expanded: true
                }
            ]);
        } catch (error) {
            message.error('Please fill all required fields in expanded panels before adding another star');
        }
    };

    const removeStar = (id) => {
        if (stars.length === 1) {
            message.error("You must have at least one star");
            return;
        }
        const remainingStars = stars.filter((star) => star.id !== id);
        if (!remainingStars.some((star) => star.expanded)) {
            remainingStars[0].expanded = true;
        }
        setStars(remainingStars);
    };

    const handleChange = (id, field, value) => {
        setStars(stars.map(star =>
            star.id === id ? { ...star, [field]: value } : star
        ));
    };

    const { data: userData, loading: userLoading, error: userError } = useQuery(Get_Not_Expert_Profile, {
        variables: { accountId },
        skip: !accountId,
        onCompleted: (data) => {
            console.log("API Success:", data);
        },
        onError: (err) => {
            console.error("API Error:", err);
            message.error("Failed to load user data for the dropdown. Please try again.");
        }
    });

    const [starSearchTerm, setStarSearchTerm] = useState("");

    const [fetchStarUsers, { data: starUsersData, loading: starUsersLoading }] = useLazyQuery(GET_NOT_EXPERT_EMPID_LOCATION_FULLNAME, {
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (accountId) {
            fetchStarUsers({ variables: { accountId: parseInt(accountId), searchTerm: "" } });
        }
    }, [accountId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (accountId) {
                fetchStarUsers({ variables: { accountId: parseInt(accountId), searchTerm: starSearchTerm } });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [starSearchTerm]);

    const starUserOptions = useMemo(() => {
        const users = starUsersData?.getNotExpertEmpidLoctaionFullname || [];
        return users.map(user => {
            const fullName = [user.name, user.surname].filter(Boolean).join(' ');
            const location = user.location || '';
            const labelParts = [fullName, user.employeeId, location].filter(Boolean);
            return { value: user.id, label: labelParts.join(' | ') };
        });
    }, [starUsersData]);

    const { data: languagesData, loading: languagesLoading } = useQuery(Get_Expert_Video);

    const [starNameToIdMap, setStarNameToIdMap] = useState({});

    useEffect(() => {
        if (userData?.getNotExpert?.length) {
            const newMap = {};
            userData.getNotExpert.forEach(user => {
                newMap[`${user.name} (${user.employeeId})`] = user.id;
            });
            setStarNameToIdMap(newMap);
        } else {
            console.log("No users found in userData:", userData);
        }
    }, [userData]);

    useEffect(() => {
        if (languagesData?.mdLanguages?.length) {
            const nameToId = {};
            const idToName = {};
            languagesData.mdLanguages.forEach(lang => {
                nameToId[lang.name.toLowerCase()] = lang.id;
                idToName[lang.id] = lang.name.toLowerCase();
            });
            setLanguageMap({ nameToId, idToName });
        }
    }, [languagesData]);

    const [languageMap, setLanguageMap] = useState({
        nameToId: {},
        idToName: {}
    });

    const convertSecondsToMMSS = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const handleSubmit = async (values) => {
        try {
            await form.validateFields();
            for (const star of stars) {
                if (!star.starId || !star.starDescription) {
                    message.error("Please fill in all required fields");
                    return;
                }
            }

            const starsData = values.stars.map((item) => {
                if (!/^\d{1,2}:\d{2}$/.test(item.duration)) {
                    throw new Error(`Invalid duration format for value: ${item.duration}`);
                }

                const [minutes, seconds] = item.duration.split(":").map(Number);
                const totalSeconds = (minutes * 60) + seconds;

                if (!item.starId) {
                    throw new Error("No valid user selected for star");
                }

                let languageId = typeof item.videoLanguage === 'number'
                    ? item.videoLanguage
                    : languageMap.nameToId[item.videoLanguage.toLowerCase()] || item.videoLanguage;

                return {
                    userId: item.starId,
                    profileSummary: item.starDescription,
                    introVideoCode: item.youtubeCode,
                    introVideoThumb: item.videoThumbnailCode,
                    phoneNumber: "0000000000",
                    introVideoTitle: item.videoTitle,
                    introVideoDescription: item.videoDescription,
                    introVideoLanguageId: languageId,
                    introVideoLengthInSeconds: totalSeconds,
                    status: 1
                };
            });

            for (const starData of starsData) {
                const { data } = await updateExpert({ variables: starData });
                if (data.updateExpert.success) {
                    console.log("Success:", data.updateExpert.message);
                } else {
                    console.error("Failed:", data.updateExpert.message);
                    message.error(data.updateExpert.message || "Failed to update star");
                }
            }

            form.resetFields();
            navigate(-1);
        } catch (error) {
            console.error("Error occurred:", error);
            message.error(error.message || "An error occurred while saving the star");
        }
    };

    useEffect(() => {
        if (!formInitialized && isEdit && editData?.stars?.length > 0) {
            const newNameToIdMap = {};
            editData.stars.forEach(star => {
                if (star.name && star.userId) {
                    newNameToIdMap[star.userId] = star.userId; // Map userId to itself for consistency
                }
            });
            setStarNameToIdMap(prev => ({ ...prev, ...newNameToIdMap }));

            const starsToSet = editData.stars.map(star => ({
                starId: star.userId,
                starDescription: star.starDescription,
                youtubeCode: star.youtubeCode,
                videoThumbnailCode: star.videoThumbnailCode,
                videoTitle: star.videoTitle,
                videoDescription: star.videoDescription,
                videoLanguage: star.videoLanguageId || star.videoLanguage,
                duration: star.formattedDuration || convertSecondsToMMSS(star.introVideoLengthInTotalSeconds || 0),
                email: star.email,
                phoneNumber: star.phoneNumber,
                designation: star.designation,
                location: star.location,
                preferredLanguages: star.preferredLanguages?.map(lang => lang.name).join(', '),
                status: 1
            }));

            form.setFieldsValue({ stars: starsToSet });
            setFormInitialized(true);
        } else if (!formInitialized && !isEdit) {
            form.setFieldsValue({ stars: [{}] });
            setFormInitialized(true);
        }
    }, [location, form, formInitialized, isEdit, editData]);

    const handleBackNavigation = () => {
        navigate(-1);
    };

    const [youtubeCodeValues, setYoutubeCodeValues] = useState({});

    const handleYoutubeCodeChange = (e, fieldIndex) => {
        const value = e.target.value;
        setYoutubeCodeValues(prev => ({
            ...prev,
            [fieldIndex]: value
        }));

        const currentValues = form.getFieldsValue();
        const currentThumbnail = currentValues.stars[fieldIndex]?.videoThumbnailCode || '';
        const isAutoGenerated = currentThumbnail.includes('https://img.youtube.com/vi/');

        if (value && (isAutoGenerated || !currentThumbnail)) {
            const thumbnailUrl = `https://img.youtube.com/vi/${value}/0.jpg`;
            const updatedStars = [...currentValues.stars];
            if (updatedStars[fieldIndex]) {
                updatedStars[fieldIndex].videoThumbnailCode = thumbnailUrl;
                form.setFieldsValue({ stars: updatedStars });
            }
        }
    };

    return (
        <div className='user-management-container' style={{ paddingTop: 90, marginTop: 0 }}>
            <div
                className="stars-add-page-header"
            >
                <button
                    type="button"
                    className="stars-add-back-button"
                    aria-label="Back to Stars"
                    onClick={handleBackNavigation}
                >
                    <img
                        src={backIcon}
                        alt="Back"
                    />
                </button>
                <h1 className="stars-add-page-title">{isEditMode ? "Edit Star" : "Add New Star"}</h1>
            </div>

            {userError && (
                <div style={{ color: 'red', marginBottom: 16 }}>
                    Failed to load users: {userError.message}
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                requiredMark={false} // Disable default left-side asterisk
                onFinish={handleSubmit}
                initialValues={{ stars }}
            >
                <Form.List name="stars">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }, index) => {
                                const star = stars[index] || {};
                                return (
                                    <div
                                        key={key}
                                        className={`star-panel ${star.expanded ? '' : 'collapsed'}`}
                                    >
                                        {!star.expanded && (
                                            <div className="star-panel-header">
                                                <h2 className="star-panel-title">
                                                    {starNameToIdMap[star.starId] || `Star ${star.id || index + 1}`}
                                                </h2>
                                                <div className="starAddRemoveandExpand">
                                                    {fields.length > 1 && (
                                                        <button
                                                            style={{ cursor: 'pointer' }}
                                                            className="starAddRemove"
                                                            onClick={() => {
                                                                remove(name);
                                                                removeStar(star.id);
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                    <img
                                                        src={expandIcon}
                                                        alt={star.expanded ? 'Collapse panel' : 'Expand panel'}
                                                        onClick={() => toggleExpand(star.id)}
                                                        aria-label={star.expanded ? 'Collapse panel' : 'Expand panel'}
                                                        aria-expanded={star.expanded}
                                                        style={{
                                                            width: '42px',
                                                            height: '42px',
                                                            padding: '4px',
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {star.expanded && (
                                            <div className="star-panel-content" style={{ marginBottom: 24 }}>
                                                <Card
                                                    title={<span style={{ fontWeight: '600' }}>About Star {index + 1}</span>}
                                                    bordered
                                                    style={{ border: '1px solid black', borderRadius: 8, backgroundColor: 'white', marginBottom: 24 }}
                                                    headStyle={{ borderBottom: 'none', padding: '16px 24px', fontSize: 16 }}
                                                    bodyStyle={{ paddingTop: 0 }}
                                                >
                                                    <Row gutter={16}>
                                                        <Col span={24}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'starId']}
                                                                label={<span>Star <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[{ required: true, message: 'Please select a star' }]}
                                                                getValueFromEvent={(selected) => selected ? selected.value : null}
                                                                getValueProps={(value) => ({
                                                                    value: starUserOptions.find(opt => opt.value === value) || (isEditMode && editData?.stars?.[index]?.userId === value ? { value: editData.stars[index].userId, label: editData.stars[index].name } : null)
                                                                })}
                                                            >
                                                                <SelectDropdown
                                                                    options={isEditMode && editData?.stars?.[index]?.name
                                                                        ? [{ value: editData.stars[index].userId, label: editData.stars[index].name }, ...starUserOptions]
                                                                        : starUserOptions
                                                                    }
                                                                    onChange={(selected) => {
                                                                        const val = selected ? selected.value : null;
                                                                        handleChange(star.id, 'starId', val);
                                                                    }}
                                                                    onInputChange={(value) => setStarSearchTerm(value)}
                                                                    onFocus={() => fetchStarUsers({ variables: { accountId: parseInt(accountId), searchTerm: "" } })}
                                                                    placeholder="Select user"
                                                                    isClearable
                                                                    isSearchable
                                                                    isDisabled={isEditMode}
                                                                    isLoading={starUsersLoading}
                                                                    filterOption={() => true}
                                                                    menuPortalTarget={document.body}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                    styles={{
                                                                        control: (base) => ({ ...base, minHeight: '32px' }),
                                                                        valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                                                        input: (base) => ({ ...base, margin: '0', padding: '0' }),
                                                                        indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                                                                        menu: (base) => ({ ...base, minWidth: '350px', zIndex: 9999 }),
                                                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                                        option: (base) => ({ ...base, fontWeight: 'normal', fontSize: '13px' }),
                                                                        singleValue: (base) => ({ ...base, fontWeight: 'normal', fontSize: '13px' }),
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={24}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'starDescription']}
                                                                label={<span>Star Description <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[{ required: true, message: 'Please enter a description' }]}
                                                            >
                                                                <TextArea
                                                                    rows={4}
                                                                    placeholder="Write Star Description…"
                                                                    onChange={(e) => handleChange(star.id, 'starDescription', e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                </Card>

                                                <Card
                                                    title="Video Details"
                                                    bordered
                                                    style={{ border: '1px solid #000', borderRadius: 8 }}
                                                    headStyle={{ borderBottom: 'none', padding: '16px 24px', fontSize: 16 }}
                                                    bodyStyle={{ paddingTop: 0 }}
                                                >
                                                    <Row gutter={16}>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'youtubeCode']}
                                                                label={<span>Video Code <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[
                                                                    { required: true, message: 'Please enter video code' },
                                                                    { min: 11, max: 11, message: 'Video code must be exactly 11 characters' }
                                                                ]}
                                                            >
                                                                <Input
                                                                    placeholder="Enter Video Code…"
                                                                    maxLength={11}
                                                                    onChange={(e) => handleYoutubeCodeChange(e, name)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'videoTitle']}
                                                                label={<span>Video Title <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[{ required: true, message: 'Please enter video title' }]}
                                                            >
                                                                <Input
                                                                    placeholder="Enter Video Title…"
                                                                    onChange={(e) => handleChange(star.id, 'videoTitle', e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'videoThumbnailCode']}
                                                                label={<span>Video Thumbnail Code <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[{ required: true, message: 'Please enter thumbnail code' }]}
                                                            >
                                                                <Input
                                                                    placeholder="Enter Thumbnail Code…"
                                                                    disabled
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'videoLanguage']}
                                                                label={<span>Video Language <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[{ required: true, message: 'Please select a language' }]}
                                                            >
                                                                <Select
                                                                    showSearch
                                                                    className="custom-select"
                                                                    placeholder="Select Language"
                                                                    loading={languagesLoading}
                                                                    optionFilterProp="children"
                                                                    filterOption={(input, option) =>
                                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                                    }
                                                                    options={languagesData?.mdLanguages?.map(lang => ({
                                                                        value: lang.id,
                                                                        label: lang.name
                                                                    }))}
                                                                    onChange={(value) => handleChange(star.id, 'videoLanguageId', value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'duration']}
                                                                label={<span>Duration <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[
                                                                    { required: true, message: 'Please enter duration' },
                                                                    {
                                                                        pattern: /^([0-5][0-9]):([0-5][0-9])$/,
                                                                        message: 'Format must be MM:SS (00:00 - 59:59)'
                                                                    }
                                                                ]}
                                                            >
                                                                <Input
                                                                    placeholder="MM:SS"
                                                                    onChange={(e) => handleChange(star.id, 'duration', e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col span={24}>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'videoDescription']}
                                                                label={<span>Video Description <span style={{ color: 'red' }}>*</span></span>}
                                                                rules={[{ required: true, message: 'Please enter video description' }]}
                                                            >
                                                                <TextArea
                                                                    rows={4}
                                                                    placeholder="Write Video Description…"
                                                                    onChange={(e) => handleChange(star.id, 'videoDescription', e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                                            e.preventDefault();
                                                                            handleSubmit();
                                                                        }
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: isEditMode ? 'flex-end' : 'space-between',
                                    alignItems: 'center',
                                    marginTop: 24,
                                    gap: 12,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {!isEditMode && (
                                    <button
                                        className='addAnotherStar'
                                        type="button"
                                        onClick={() => addAnotherStar(add)}
                                        style={{ display: 'flex', marginBottom: 0 }}
                                    >
                                        Add Another
                                    </button>
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', columnGap: '10px' }}>
                                    <button
                                        
                                        type="button"
                                        onClick={() => setDiscardModalVisible(true)}
                                        style={{
                                            border: "1px solid #828282",
                                            backgroundColor: "#FFFFFF",
                                            color: "#333333",
                                            borderRadius: "4px",
                                            width: "100px",
                                            height: "35px",
                                            textAlign: "center",
                                            
                                        }}
                                    >
                                        Discard
                                    </button>
                                    <button className='addStarSave' type="submit" disabled={loading}>
                                        {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Save and Create"}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </Form.List>

                <Modal
                    open={discardModalVisible}
                    footer={[
                        <div key="footer-buttons" style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                            width: '100%',
                        }}>
                            <button
                                className="starsAddModal"
                                onClick={() => {
                                    form.resetFields();
                                    setDiscardModalVisible(false);
                                    navigate(-1);
                                }}
                                disabled={loading}
                                style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                            >
                                {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Yes'}
                            </button>
                            <button
                                className="starsAddModalCancel"
                                onClick={() => setDiscardModalVisible(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    ]}
                    centered
                    onCancel={() => setDiscardModalVisible(false)}
                >
                    <p style={{ fontFamily: "Inter", fontWeight: "600", fontSize: "15px" }}>
                        Do you want to discard changes?
                    </p>
                </Modal>
            </Form>
        </div>
    );
};

export default StarsAdd;